import BrazeUtil from '@/utility/braze-util.ts';
import type { ExpertDetailInfoResponse } from '@dopingtech/live_expert_bff_api_ts/api.ts';

const BRAZE_SYNC_KEY = 'braze_attributes_last_sync';
const SYNC_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Checks if Braze attributes need to be re-synced (every 24 hours).
 */
function shouldSyncAttributes(): boolean {
	const lastSync = localStorage.getItem(BRAZE_SYNC_KEY);
	if (!lastSync) return true;

	const elapsed = Date.now() - parseInt(lastSync, 10);
	return elapsed >= SYNC_INTERVAL_MS;
}

/**
 * Syncs expert detail info as Braze custom attributes.
 * Sends all 24 requested attributes and records sync timestamp.
 */
export function syncBrazeExpertAttributes(data?: ExpertDetailInfoResponse | null): void {
	if (!data) {
		console.warn('[Braze] No expert data provided, skipping attribute sync.');
		return;
	}

	if (!shouldSyncAttributes()) {
		console.log('[Braze] Attributes already synced within the last 24 hours, skipping.');
		return;
	}

	try {
		const expert = data?.expert;
		const specialization = data?.specialization;
		const branchList = data?.branchList ?? [];
		const curriculumList = data?.curriculumList ?? [];
		const serviceList = data?.serviceList ?? [];
		const educationLevelList = data?.educationLevelList ?? [];

		if (expert) {
			BrazeUtil?.setCustomUserAttribute('expert_id', expert?.id ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_applicant_id', expert?.applicantId ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_user_id', expert?.expertUserId ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_name', expert?.name ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_surname', expert?.surname ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_birthday', expert?.birthday ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_email', expert?.email ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_phone', expert?.phone ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_specialization_type', expert?.specializationType ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_school_type', expert?.schoolType ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_application_status', expert?.applicationStatus ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_department_id', expert?.departmentId ?? null);
			BrazeUtil?.setCustomUserAttribute('expert_department_name', expert?.departmentName ?? null);
		}

		if (specialization) {
			BrazeUtil?.setCustomUserAttribute('specialization_name', specialization?.name ?? null);
			BrazeUtil?.setCustomUserAttribute('specialization_description', specialization?.description ?? null);
		}

		BrazeUtil?.setCustomUserAttribute(
			'branch_ids',
			branchList.map(b => String(b?.branchId ?? ''))
		);
		BrazeUtil?.setCustomUserAttribute(
			'branch_names',
			branchList.map(b => b?.branchName ?? '')
		);
		BrazeUtil?.setCustomUserAttribute(
			'branch_lesson_ids',
			branchList.map(b => String(b?.lessonId ?? ''))
		);
		BrazeUtil?.setCustomUserAttribute(
			'branch_lesson_names',
			branchList.map(b => b?.lessonName ?? '')
		);

		BrazeUtil?.setCustomUserAttribute(
			'curriculum_ids',
			curriculumList.map(c => String(c?.curriculumId ?? ''))
		);
		BrazeUtil?.setCustomUserAttribute(
			'curriculum_names',
			curriculumList.map(c => c?.name ?? '')
		);

		BrazeUtil?.setCustomUserAttribute(
			'service_type_ids',
			serviceList.map(s => String(s?.liveServiceTypeId ?? ''))
		);
		BrazeUtil?.setCustomUserAttribute(
			'service_statuses',
			serviceList.map(s => s?.expertServiceStatus ?? '')
		);

		BrazeUtil?.setCustomUserAttribute(
			'education_levels',
			educationLevelList.map(e => e?.name ?? '')
		);

		localStorage.setItem(BRAZE_SYNC_KEY, Date.now().toString());
		console.log('[Braze] Expert custom attributes synced successfully.');
	} catch (error) {
		console.error('[Braze] Failed to sync expert custom attributes:', error);
	}
}
